import { action, computed, observable } from 'mobx'
import { memoizeAsync } from '@/utils/memoize-decorator'
import { differenceInDays, toDate } from 'date-fns'
import { merge, type Observable } from 'rxjs'
import { distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators'
import { FeatureToggles } from '@/features/feature-toggles'
import { type RoomEvent } from '@/features/messaging/rooms/room-event'
import type {
  EnsureTermsAcceptedFn,
  GetCurrentMemberFn,
  GetTeamMemberFn,
  GetUserDataFn,
} from '@/features/iam'
import { Member } from '@/features/iam/members/Member'
import type { Permissions } from '@/features/iam/permissions/Permissions'
import type { WellKnownDocumentTypeShortId } from '@/features/documents/DocumentType'
import type { NavigateFn } from '@/RootStore'
import { type ActivationLifecycle, activeWhen } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { Id } from '@/infra/id'
import { isRefreshDesign } from '@/infra/design'
import { localStorage } from '@/infra/storage'
import { timeago } from '@/utils/date-util'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { links } from '@/utils/link'
import { isNotNil } from '@/utils/object-util'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { type JobScopeChangeStore } from '@/features/jobs/job-scope-changes/JobScopeChangeStore'
import { statusGlyph } from '@/features/jobs/job-status-display'
import { formatWithLegendPriceUnit } from '@/features/jobs/legends/legend-price-unit'
import { type JobConversationViewModel } from '@/features/jobs/details/JobConversationViewModel'
import { type JobCollaboratorsViewModel } from '@/features/jobs/details/JobCollaboratorsViewModel'
import { type JobBillingViewModel } from '@/features/jobs/details/JobBillingViewModel'
import { type JobScopeChangesViewModel } from '@/features/jobs/details/JobScopeChangesViewModel'
import { type JobCapturedInformationViewModel } from '@/features/jobs/details/JobCapturedInformationViewModel'
import { type JobConsultationViewModel } from '@/features/jobs/details/JobConsultationViewModel'
import { type JobBookkeepingViewModel } from '@/features/jobs/details/JobBookkeepingViewModel'
import { type JobActionsViewModel } from '@/features/jobs/details/JobActionsViewModel'
import { type JobDocumentsViewModel } from '@/features/jobs/details/JobDocumentsViewModel'
import { type JobTicketsViewModel } from '@/features/jobs/details/JobTicketsViewModel'
import { type DetailsViewModel } from '@/features/jobs/details/DetailsViewModel'
import { type JobNotesViewModel } from '@/features/jobs/details/components/JobNotesCard/JobNotesViewModel'
import { type JobInsightsCardViewModel } from '@/features/jobs/details/components/JobInsightsCard/JobInsightsCardViewModel'
import { type JobProgressionViewModel } from '@/features/jobs/details/components/JobProgressionCard/JobProgressionViewModel'
import { type FinalReturnAmountsCardViewModel } from '@/features/jobs/details/components/FinalReturnAmountsCard/FinalReturnAmountsCardViewModel'
import { OverviewPaneViewModel } from '@/features/jobs/details/components/JobSections/OverviewPane/OverviewPaneViewModel'

const otherDocumentTypeShortId: WellKnownDocumentTypeShortId = 'OTHER'

/**
 * The card that shows the job's current step.
 */
export type JobStepCard =
  | 'Bookkeeping'
  | 'CompleteOnboarding'
  | 'InfoGathering'
  | 'JobProgressionConsultation'
  | 'ConsultationCall'
  | 'JobProgression'
  | 'Task'

/**
 * The latest status change, split around its relative time.
 */
export interface StatusHistorySummaryType {
  prefix: string | null
  time: string
  suffix: string | null
}

export type JobSection =
  'overview' | 'documents' | 'intake' | 'notes' | 'activity'

export interface JobSectionTab {
  id: JobSection
  label: string
  count?: string
}

/**
 * Sidebar tab.
 */
export type JobTab = 'Details' | 'Documents' | 'Collaborators' | 'Communication'

/**
 * Manages the state of the Job view.
 */
export class JobDetailsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The current tab in the sidebar.
   */
  @observable accessor currentTab: JobTab = 'Details'

  /**
   * The tab showing in the job's main pane.
   */
  @observable accessor section: JobSection = 'overview'

  /**
   * Whether the job's properties rail is open. Remembered per browser so it
   * stays put while moving between jobs.
   */
  @observable accessor detailsOpen = readDetailsOpen()

  readonly overviewVm: OverviewPaneViewModel

  constructor(
    public readonly job: Job,
    private readonly _navigate: NavigateFn,
    private readonly _jobStore: JobStore,
    jobScopeChangeStore: JobScopeChangeStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getTeamMember: GetTeamMemberFn,
    private readonly _getUserData: GetUserDataFn,
    private readonly _ensureTermsAccepted: EnsureTermsAcceptedFn,
    private readonly _requestClose: (job: Job) => unknown,
    private readonly _roomEvents$: Observable<RoomEvent>,
    public readonly jobConversationVm: JobConversationViewModel,
    public readonly documentsVm: JobDocumentsViewModel,
    public readonly detailsVm: DetailsViewModel,
    public readonly jobNotesVm: JobNotesViewModel,
    public readonly jobTicketsVm: JobTicketsViewModel,
    public readonly jobInsightsVm: JobInsightsCardViewModel,
    public readonly finalReturnAmountsVm: FinalReturnAmountsCardViewModel,
    public readonly jobProgressionVm: JobProgressionViewModel,
    public readonly collaboratorsVm: JobCollaboratorsViewModel,
    public readonly billingVm: JobBillingViewModel,
    public readonly scopeChangesVm: JobScopeChangesViewModel,
    public readonly capturedInformationVm: JobCapturedInformationViewModel,
    public readonly consultationVm: JobConsultationViewModel,
    public readonly bookkeepingVm: JobBookkeepingViewModel,
    public readonly actionsVm: JobActionsViewModel,
  ) {
    this.overviewVm = new OverviewPaneViewModel(
      documentsVm,
      capturedInformationVm,
    )

    this._lifecycle
      .manage(jobConversationVm)
      .manage(documentsVm)
      .manage(detailsVm)
      .manage(jobTicketsVm)
      .manage(collaboratorsVm)
      .manage(billingVm)
      .manage(scopeChangesVm)
      .manage(capturedInformationVm)
      .manage(actionsVm)
      .manage(activeWhen(() => this.canViewJobNotes, jobNotesVm))
      .manage(activeWhen(() => this.canViewJobInsights, jobInsightsVm))
      .manage(activeWhen(() => this.isProgressionFlowEnabled, jobProgressionVm))
      .manage(
        activeWhen(
          () => bookkeepingVm.isBookkeepingEnabled,
          bookkeepingVm.bookkeepingProgressionVm,
        ),
      )
      .manage(
        _roomEvents$.pipe(
          filter((e) => e.type !== 'Unknown'),
          filter((e) => e.room.conversation.id === this.job.conversation?.id),
          filter((e) => e.room.id !== this.job.consultation?.room.id),
          // A room event for the job's conversation means our copy of the job
          // may be stale. A failed refetch keeps the copy we have.
          switchMap(() =>
            withAbortSignal((signal) =>
              this._jobStore
                .fetchJob(this.job.id, { force: true }, signal)
                .catch(() => undefined),
            ),
          ),
        ),
      )
      .manage(
        merge(
          merge(
            jobScopeChangeStore.jobScopeChangeApproved$,
            jobScopeChangeStore.adminJobScopeUpdated$,
          ).pipe(filter((e) => e.jobId === job.id)),
          toStream(() => job.status, true).pipe(distinctUntilChanged()),
        ).pipe(
          switchMap(() =>
            withAbortSignal((signal) =>
              this._jobStore
                .fetchJob(this.job.id, { force: true }, signal)
                .catch(() => undefined),
            ),
          ),
        ),
      )
      .manage(
        toStream(() => this.visibleCard === 'CompleteOnboarding', true).pipe(
          filter(Boolean),
          tap(() => detailsVm.completeOnboarding.set(true)),
        ),
      )
  }

  /**
   * Computes whether an active PTIN is required for the current member.
   */
  @computed
  get ptinRequired(): boolean {
    const featureEnabled = FeatureToggles.requireActivePtin()
    if (!featureEnabled) {
      return false
    }

    const userData = this._getUserData(this._getCurrentMember().user.publicId)

    return (
      userData?.userDataType === 'Pro' && userData?.ptinStatus === 'Inactive'
    )
  }

  /**
   * Whether the job is being viewed.
   */
  get isActive() {
    return this._lifecycle.active
  }

  /**
   * Whether the current user is a Pro.
   */
  @computed
  get isPro() {
    return (
      this._getUserData(this._getCurrentMember().user.publicId)
        ?.userDataType === 'Pro'
    )
  }

  /**
   * The conversation the chat shows.
   */
  get conversationVm() {
    return this.jobConversationVm.conversationVm
  }

  get messageBankDialogVm() {
    return this.jobConversationVm.messageBankDialogVm
  }

  get documentLinkingPickerVm() {
    return this.documentsVm.documentLinkingPickerVm
  }

  /**
   * The workspace that the job belongs to.
   */
  @computed
  get jobWorkspace() {
    return this.job.workspace
  }

  /**
   * The workspace member for the current user on the workspace
   * that the job belongs to.
   */
  @computed
  get jobWorkspaceMember() {
    return this._getCurrentMember(this.jobWorkspace.id) ?? Member.none
  }

  /**
   * The member the conversation's header shows.
   */
  @computed
  get participantMemberToDisplay() {
    return this.collaboratorsVm.participantMemberToDisplay
  }

  @computed
  get championClient() {
    return this.collaboratorsVm.championClient
  }

  /**
   * The member whose avatar the conversation's header shows.
   */
  @computed
  get chatAvatarMember() {
    return (
      this.participantMemberToDisplay ??
      this.championClient?.collaborator.member
    )
  }

  @computed
  get chatDisplayName() {
    return (
      this.participantMemberToDisplay?.displayName ??
      this.championClient?.collaborator.member.displayName
    )
  }

  /**
   * Whether the job's champion client is online, whoever the header shows.
   */
  @computed
  get isChatClientOnline() {
    return !!this.championClient?.collaborator.member.user.online
  }

  @computed
  get chatLastSeenText() {
    const participant = this.participantMemberToDisplay
    return participant ? timeago(participant.lastSeen ?? new Date()) : null
  }

  /**
   * The document types the composer offers by name; the catch-all type is
   * offered separately as {@link chatOtherDocumentType}.
   */
  @computed
  get chatDocumentTypes() {
    return this.documentsVm.documentTypesForUser.filter(
      (t) => t.id !== otherDocumentTypeShortId,
    )
  }

  @computed
  get chatOtherDocumentType() {
    return this.documentsVm.documentTypesForUser.find(
      (t) => t.id === otherDocumentTypeShortId,
    )
  }

  @computed
  get canUploadOtherChatDocuments() {
    return this.chatOtherDocumentType !== undefined
  }

  /**
   * Stages files added in the chat composer as documents of the given type.
   */
  @action.bound
  stageChatDocuments(files: FileList, typeId: Id) {
    const type = this.chatDocumentTypes.find((t) => t.id === typeId)
    return type
      ? this.documentsVm.stageFileAsType(files, type)
      : Promise.resolve(false)
  }

  /**
   * Stages files added in the chat composer as the catch-all document type.
   */
  @action.bound
  stageOtherChatDocuments(files: FileList) {
    return this.chatOtherDocumentType
      ? this.documentsVm.stageFileAsType(files, this.chatOtherDocumentType)
      : Promise.resolve(false)
  }

  @computed
  get canUpdateJobProgressionAsClient() {
    return (
      this.collaboratorsVm.championClient?.isCurrentUser ||
      isNotNil(this.currentTeamMemberOfOwnerTeam)
    )
  }

  @computed
  get isClientTeamJob() {
    return this.job.isClientTeamJob
  }

  /**
   * Formats the legend name and the legend version.
   */
  @computed
  get legendText() {
    return `${this.job.legendName} V${this.job.legendVersionId.version}`
  }

  /**
   * Whether the new job progression config is being used.
   */
  @computed
  get usingNewJobProgressionConfig() {
    return FeatureToggles.updatedJobProgressionConfig()
  }

  /**
   * Whether insights workspace config is enabled.
   */
  @computed
  get isInsightsWorkspaceConfigEnabled() {
    return this.jobWorkspace.config.insights.enabled
  }

  @computed
  get isOnHold() {
    return this.job.isOnHold
  }

  @computed
  get isClaimedJob() {
    return this.job.isClaimedJob
  }

  @computed
  get completed() {
    return this.job.completed
  }

  /**
   * Whether the current user is a Provider-role collaborator.
   */
  @computed
  get isProviderCollaborator() {
    return this.job.isProviderCollaborator
  }

  /**
   * Whether the current user is a champion Client-role collaborator.
   */
  @computed
  get isChampionClientCollaborator() {
    return this.job.isChampionClientCollaborator
  }

  /**
   * Whether the current user can approve the draft as a supporting client.
   */
  @computed
  get canApproveDraftAsSupportingClient() {
    const currentMember = this.collaboratorsVm.supportingClients.find(
      (c) => c.member.isCurrentUser,
    )

    return currentMember?.member.permissions
      .supportingClientJobProgressionApproveDraft
  }

  /**
   * Whether the current user reviews the draft as an admin. Champion and
   * supporting clients get their own review actions.
   */
  @computed
  get canReviewDraftAsAdmin() {
    return (
      !this.isChampionClientCollaborator &&
      !this.canApproveDraftAsSupportingClient
    )
  }

  /**
   * Whether the progression is described to the current user as a client.
   */
  @computed
  get seesClientProgression() {
    return Boolean(
      this.isChampionClientCollaborator ||
      this.canApproveDraftAsSupportingClient,
    )
  }

  @computed
  get canSetReadyToFile() {
    return this.jobWorkspaceMember.permissions.adminJobProgressionApproveDraft
  }

  /**
   * Whose move the current progression step is, or `null` once the job is
   * done.
   */
  @computed
  get progressionTurn(): 'client' | 'pro' | null {
    if (this.job.completed) {
      return null
    }

    switch (this.jobProgressionVm.currentStepStatus) {
      case 'DraftInReview':
      case 'ProgressionWaitingOnClient':
        return 'client'
      default:
        return 'pro'
    }
  }

  /**
   * Whether the current user can take the current progression step, mirroring
   * which step actions they are shown.
   */
  @computed
  get canTakeProgressionStep() {
    const step = this.jobProgressionVm.currentStepStatus
    switch (this.progressionTurn) {
      case 'client':
        return (
          this.isChampionClientCollaborator ||
          (step === 'DraftInReview' &&
            Boolean(this.canApproveDraftAsSupportingClient)) ||
          (this.canSetReadyToFile &&
            (step === 'ProgressionWaitingOnClient' ||
              this.canReviewDraftAsAdmin))
        )
      case 'pro':
        return this.isProviderCollaborator
      default:
        return false
    }
  }

  /**
   * Who the current progression step waits on when it isn't the current
   * user's to take.
   */
  @computed
  get progressionWaitingOn() {
    if (this.canTakeProgressionStep) {
      return null
    }

    switch (this.progressionTurn) {
      case 'client':
        return this.job.championClient?.member ?? null
      case 'pro':
        return this.job.championProvider?.member ?? null
      default:
        return null
    }
  }

  /**
   * Current team member of owner team.
   */
  @computed
  get currentTeamMemberOfOwnerTeam() {
    if (!this.job.ownerTeam) {
      return
    }

    return this._getTeamMember(
      this.job.ownerTeam.id,
      this.jobWorkspaceMember.user.publicId,
    )
  }

  /**
   * If the current user is a part of the owner team.
   */
  @computed
  get isPartOfClientTeam() {
    return Boolean(this.currentTeamMemberOfOwnerTeam)
  }

  /**
   * If the current user is a part of the pro team.
   */
  @computed
  get isPartOfProTeam() {
    const proTeam = this.job.collaborators.find(
      (c) => c.type === 'Pro' && c.team !== null,
    )?.team

    return Boolean(
      proTeam?.id &&
      this._getTeamMember(proTeam.id, this.jobWorkspaceMember.user.publicId),
    )
  }

  /**
   * Whether the current user is on the Client side of the job, either as the
   * champion Client collaborator or as a member of the owner team.
   */
  @computed
  get isClientCollaborator() {
    return this.job.isChampionClientCollaborator || this.isPartOfClientTeam
  }

  /**
   * The permissions the current user holds on the job: those of their team
   * members on the job's teams, or their workspace member's when they are on
   * none of them. Collaborator checks still apply on top of these.
   */
  @computed
  get teamOrMemberPermissions(): Permissions[] {
    const currentMember = this.jobWorkspaceMember
    const teamMembers = this.job.teams
      .map((team) => this._getTeamMember(team.id, currentMember.user.publicId))
      .filter(isNotNil)
    return teamMembers.length > 0
      ? teamMembers.map((tm) => tm.permissions)
      : [currentMember.permissions]
  }

  /**
   * Whether the current user is allowed to view the Legend name.
   */
  @computed
  get canViewLegendNameAsAdmin() {
    return this.jobWorkspaceMember.permissions.adminLegendView
  }

  /**
   * Whether the user has the permission to view the pro cut as admin
   */
  @computed
  get canViewProCut() {
    return this.jobWorkspaceMember.permissions.adminProCutView
  }

  /**
   * Whether to show the earnings.
   */
  @computed
  get shouldShowProviderCutAmount() {
    const isProviderOrAdmin =
      this.job.isProviderCollaborator || this.canViewProCut

    return isProviderOrAdmin && this.job.providerCutAmount > 0
  }

  @computed
  get shouldShowJobTotal() {
    const { permissions } = this.jobWorkspaceMember

    const isClientCollaborator = this.job.collaborators.some(
      (c) => c.type === 'Client' && c.member.isCurrentUser,
    )

    const onOwnerTeamWithViewPricing = Boolean(
      this.currentTeamMemberOfOwnerTeam?.permissions.viewJobPricing,
    )

    return (
      (isClientCollaborator && permissions.viewJobPricing) ||
      onOwnerTeamWithViewPricing
    )
  }

  /**
   * Whether the user can upload documents to the job
   */
  @computed
  get canUploadDocs() {
    const { permissions } = this.jobWorkspaceMember
    return (
      (permissions.docsUpload || permissions.adminDocumentManage) &&
      this.job.status !== 'Closed'
    )
  }

  /**
   * Whether the user can view the documents
   */
  @computed
  get canViewDocs() {
    const { permissions } = this.jobWorkspaceMember
    return permissions.docsView || permissions.adminDocumentManage
  }

  /**
   * Whether the user can remove documents
   */
  @computed
  get canRemoveDocs() {
    return this.documentsVm.canRemoveDocs
  }

  /**
   * Whether the user can download documents
   */
  @computed
  get canDownloadDocs() {
    return this.jobWorkspaceMember.permissions.docsUpload
  }

  @computed
  get hasAnyDocPermission() {
    return this.canViewDocs || this.canUploadDocs
  }

  /**
   * Whether there are selected documents
   */
  @computed
  get hasDocumentsSelected() {
    return this.documentsVm.selectedDocuments.size > 0
  }

  /**
   * Amount of days left to complete the job
   */
  @computed
  get daysLeft() {
    if (this.job.dateDeadline === null) {
      return ' '
    }
    const diff = differenceInDays(this.job.dateDeadline, toDate(Date.now()))
    if (Math.abs(diff) === 1) {
      return diff > 0 ? `1 day` : `1 day ago`
    }
    return diff > 0 ? `${diff} days` : `${diff * -1} days ago`
  }

  /**
   * Job duration stat depending on Job Status.
   */
  @computed
  get statusHistorySummary(): StatusHistorySummaryType | null {
    if (
      this.job.status === 'UnderConstruction' &&
      this.job.dateCreated !== null
    ) {
      const timeAgo = timeago(this.job.dateCreated, 'long')
      const isRightNow = timeAgo === 'now'

      return {
        prefix: 'Job created',
        time: isRightNow ? `right ${timeAgo}` : timeAgo,
        suffix: isRightNow ? '' : 'ago',
      }
    }

    if (
      (this.job.status === 'OnHold' || this.job.status === 'Idle') &&
      this.job.dateLastActivity !== null
    ) {
      const timeAgo = timeago(this.job.dateLastActivity, 'long')
      const isRightNow = timeAgo === 'now'

      return {
        prefix: 'No activity since',
        time: isRightNow ? `right ${timeAgo}` : timeAgo,
        suffix: isRightNow ? '' : 'ago',
      }
    }

    if (this.job.status !== 'Closed' && this.job.dateAccepted !== null) {
      const timeAgo = timeago(this.job.dateAccepted, 'long')
      const isRightNow = timeAgo === 'now'

      return {
        prefix: 'Job accepted',
        time: isRightNow ? `right ${timeAgo}` : timeAgo,
        suffix: isRightNow ? '' : 'ago',
      }
    }

    if (
      this.job.status === 'Closed' &&
      this.job.dateAccepted !== null &&
      this.job.dateClosed !== null
    ) {
      const timeAgo = timeago(
        this.job.dateAccepted,
        'long',
        this.job.dateClosed,
      )
      const isRightNow = timeAgo === 'now'

      return {
        prefix: 'Job took',
        time: isRightNow ? 'less then a second' : timeAgo,
        suffix: 'to complete',
      }
    }

    if (this.job.status === 'Unclaimed' && this.job.dateSubmitted !== null) {
      const timeAgo = timeago(this.job.dateSubmitted, 'long')
      const isRightNow = timeAgo === 'now'

      return {
        prefix: 'Job submitted',
        time: isRightNow ? `right ${timeAgo}` : timeAgo,
        suffix: isRightNow ? '' : 'ago',
      }
    }

    return null
  }

  @computed
  get formattedStatusHistorySummary() {
    const summary = this.statusHistorySummary
    return summary
      ? [summary.prefix, summary.time, summary.suffix].filter(Boolean).join(' ')
      : null
  }

  /**
   * The status glyph and its tone.
   */
  @computed
  get statusGlyph() {
    return statusGlyph({
      status: this.job.status,
      resolution: this.job.resolution,
      progressionStep: this.jobProgressionVm.currentStepStatus,
    })
  }

  /**
   * The provider's cut, in the legend's price unit.
   */
  @computed
  get formattedProviderCutAmount() {
    if (!this.shouldShowProviderCutAmount) {
      return ''
    }

    // With a payout split, the current user sees their own allocation;
    // otherwise the full cut, as the user is likely an admin.
    const amount = this.job.currentUserAllocation ?? this.job.providerCutAmount

    return formatWithLegendPriceUnit(
      { priceUnit: this.job.legendPriceUnit ?? undefined },
      amount,
    )
  }

  /**
   * Whether the user can view the job's insights.
   */
  @computed
  get canViewJobInsights() {
    const insightsWorkspaceConfig = this.isInsightsWorkspaceConfigEnabled
    const insightsPermission = this.jobWorkspaceMember.permissions.insightsView
    const isJobNotUnclaimedOrUnderConstruction =
      this.job.status !== 'InfoGathering' &&
      this.job.status !== 'Pending' &&
      this.job.status !== 'Unclaimed' &&
      this.job.status !== 'UnderConstruction'

    return (
      insightsWorkspaceConfig &&
      insightsPermission &&
      isJobNotUnclaimedOrUnderConstruction
    )
  }

  /**
   * Whether any of the cards only admins see shows.
   */
  @computed
  get hasAdminCards() {
    return (
      this.canViewJobInsights ||
      this.billingVm.canViewPaymentDistributionAsAdmin
    )
  }

  /**
   * Whether the job has a conversation to chat in.
   */
  @computed
  get hasConversation() {
    return Boolean(this.job.conversation)
  }

  /**
   * Whether the user muted the job's conversation.
   */
  @computed
  get isConversationMuted() {
    return this.hasConversation && this.conversationVm.muted
  }

  /**
   * Whether the user can download the job's conversation.
   */
  @computed
  get canDownloadConversation() {
    return Boolean(
      this.job.conversation !== null &&
      this.teamOrMemberPermissions.some((p) => p.chatView),
    )
  }

  /**
   * Whether the user can view the job's notes.
   */
  @computed
  get canViewJobNotes() {
    return FeatureToggles.jobNotes() && this.jobNotesVm.canView
  }

  /**
   * Whether the user can see the conversation at all.
   */
  @computed
  get canViewChat() {
    return this.jobWorkspaceMember.permissions.chatView
  }

  /**
   * Whether the user can only read the conversation.
   */
  @computed
  get isChatReadOnly() {
    const member = this.jobWorkspaceMember
    return (
      this.job.status === 'Closed' ||
      !(
        (member.permissions.chatAdd && this._isCurrentMemberInConversation) ||
        member.permissions.adminConversationManage ||
        // Owner team members may send messages without being participants.
        Boolean(this.currentTeamMemberOfOwnerTeam?.permissions.chatAdd)
      )
    )
  }

  /**
   * Whether the job progression flow is enabled.
   */
  @computed
  get isProgressionFlowEnabled() {
    if (this.job.jobType?.type !== 'Tax') {
      return false
    }

    if (this.usingNewJobProgressionConfig) {
      const config = this.jobWorkspace.config.jobProgression
      const legendId = this.job.legendId
      const flow = Id.some(legendId) ? config.legends[legendId] : undefined
      return config.enabled && Boolean(flow) && flow !== 'None'
    }

    return this.jobWorkspace.config.jobs.progressionFlow
  }

  /**
   * Whether the user can see the last seen information in the chat.
   */
  @computed
  get canViewLastSeenInfoInChat() {
    const { permissions } = this.jobWorkspaceMember
    return permissions.adminUserRead || permissions.adminUserModify
  }

  /**
   * Determine that we are gathering that info for the job.
   */
  @computed
  get isInfoGatheringStep() {
    return this.job.status === 'InfoGathering'
  }

  @computed
  get canCompleteJobProgression() {
    return (
      this.isProgressionFlowEnabled &&
      !this.scopeChangesVm.isScopeChangeRequestPending
    )
  }

  /**
   * Whether the current user can override the job progression flow.
   */
  @computed
  get canOverrideJobProgressionStep() {
    return (
      !this.completed &&
      this.jobProgressionVm.hasStarted &&
      this.jobProgressionVm.overrideableProgressionSteps.length > 0 &&
      this.jobWorkspaceMember.permissions.adminJobUpdate
    )
  }

  /**
   * The card that shows the job's current step.
   */
  @computed
  get visibleCard(): JobStepCard {
    if (
      this.job.status !== 'UnderConstruction' &&
      this.bookkeepingVm.bookkeepingProgressionVm.isBookkeepingEnabled
    ) {
      return 'Bookkeeping'
    }

    if (this.showDraftView) {
      return 'CompleteOnboarding'
    }

    if (this.isInfoGatheringStep) {
      return 'InfoGathering'
    }

    if (this.consultationVm.showConsultationCard) {
      if (this.isProgressionFlowEnabled) {
        return 'JobProgressionConsultation'
      }
      return 'ConsultationCall'
    }

    if (this.isProgressionFlowEnabled) {
      return 'JobProgression'
    }

    return 'Task'
  }

  /**
   * Whether the current user has anything to act on next. The task card only
   * offers completing the job.
   */
  @computed
  get hasNextStep() {
    if (this.visibleCard !== 'Task' || this.consultationVm.showCallCard) {
      return true
    }

    return Boolean(this.actionsVm.canCloseAsCollaborator)
  }

  /**
   * Pros and admins think about a job by client, so the client leads and the
   * form sits above it; a client sees their own job by its name.
   */
  @computed
  get heading(): { eyebrow: string; title: string } {
    const viewerIsClient = this.job.currentUserCollaborator?.type === 'Client'
    const clientName = this.job.championClient?.member.displayName
    if (viewerIsClient || !clientName) {
      return { eyebrow: this.job.workspace.name, title: this.job.displayName }
    }
    return { eyebrow: this.job.displayName, title: clientName }
  }

  @computed
  get canChat() {
    return this.job.status !== 'UnderConstruction' && !this.showDraftView
  }

  @computed
  get isChatOpen() {
    return this.currentTab === 'Communication'
  }

  @computed
  get sectionTabs(): JobSectionTab[] {
    const tabs: JobSectionTab[] = [{ id: 'overview', label: 'Overview' }]
    if (this.canViewDocs || this.documentsVm.txfJobVm.canView) {
      tabs.push({
        id: 'documents',
        label: 'Documents',
        count: this.canViewDocs
          ? countOrNothing(this.documentsVm.documentCount)
          : undefined,
      })
    }
    tabs.push({
      id: 'intake',
      label: 'Intake answers',
      count: countOrNothing(this.intakeAnswerCount),
    })
    if (this.canViewJobNotes) {
      tabs.push({
        id: 'notes',
        label: 'Notes',
        count: this.jobNotesVm.countLabel,
      })
    }
    tabs.push({ id: 'activity', label: 'Activity' })
    return tabs
  }

  /**
   * The selected section, or the overview when the selected one isn't
   * available to this user.
   */
  @computed
  get currentSection(): JobSection {
    return this.sectionTabs.some((t) => t.id === this.section)
      ? this.section
      : 'overview'
  }

  /**
   * Answered questions, leaving out the questionnaire's section breaks.
   */
  @computed
  get intakeAnswerCount() {
    const questions = this.detailsVm.responses.questionVms.filter(
      (q) => q.editor.type !== 'Breaker',
    )
    return questions.length || this.job.answers.length
  }

  @computed
  get canAskClient() {
    return this.isProviderCollaborator && !this.showDraftView
  }

  /**
   * Whether the user should see the draft job details.
   */
  @computed
  get showDraftView() {
    return this.job.isDraft
  }

  /**
   * Whether the job's questions has ben completed
   */
  @computed
  get questionsCompleted() {
    return this.detailsVm.questionsCompleted
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.finalReturnAmountsVm.breakdownModal.close()
  }

  /**
   * Fetches what the job view shows that its own view models don't.
   * This is only called once per job.
   */
  @memoizeAsync()
  async fetch(signal?: AbortSignal) {
    try {
      await this.collaboratorsVm.fetchTeamMemberForCurrentUser(signal)
      signal?.throwIfAborted()
      await this._ensureTermsAcceptedForJob()
      signal?.throwIfAborted()
      await this.billingVm.fetch(signal)
    } catch {
      // The job view is usable without these.
    }
  }

  /**
   * Set the current tab.
   * @param tab
   */
  @action.bound
  setTab(tab: JobTab) {
    this.currentTab = tab
  }

  @action.bound
  showDetailsTab() {
    this.setTab('Details')
  }

  @action.bound
  showDocumentsTab() {
    this.setTab('Documents')
  }

  @action.bound
  setSection(section: JobSection) {
    this.section = section
  }

  @action.bound
  viewDocumentsSection() {
    this.setSection('documents')
  }

  @action.bound
  viewNotesSection() {
    this.setSection('notes')
  }

  @action.bound
  composeNote() {
    if (!this.jobNotesVm.canWrite) {
      return
    }

    this.setSection('notes')
    this.jobNotesVm.addNoteVm.focusComposer()
  }

  @action.bound
  toggleChat() {
    this.setTab(this.isChatOpen ? 'Details' : 'Communication')
  }

  @action.bound
  openChat() {
    this.setTab('Communication')
  }

  @action.bound
  closeChat() {
    this.setTab('Details')
  }

  @action.bound
  viewPrepAndReviewJob() {
    this._navigate(links.job(String(this.job.prepAndReviewJobLinkId)))
  }

  /**
   * Opens the notes section and highlights it. The classic layout keeps the
   * notes card on the page, so it only needs the highlight.
   */
  @action.bound
  revealNotesFromChat() {
    if (isRefreshDesign) {
      this.setSection('notes')
    }
    this.jobNotesVm.revealFromChat()
  }

  /**
   * Copies the job's short ID to the clipboard.
   */
  @action.bound
  copyShortId() {
    void navigator.clipboard?.writeText(this.job.shortId).catch(() => undefined)
  }

  @action.bound
  toggleDetails() {
    this.detailsOpen = !this.detailsOpen
    localStorage.setItem(detailsOpenKey, String(this.detailsOpen))
    if (!this.detailsOpen) {
      this.finalReturnAmountsVm.breakdownModal.close()
    }
  }

  /**
   * Requests to show the close dialog for use by non-admins.
   */
  @action.bound
  requestCloseAsCollaborator() {
    // Criteria for establishing that a job is a tax-type job with an incomplete consultation.
    const taxTypeJobWithIncompleteConsultation =
      this.isProgressionFlowEnabled &&
      this.job.jobType?.type === 'Tax' &&
      this.consultationVm.showConsultationCard &&
      !this.job.consultation?.hadRoomSession

    // Prevent closing tax-type jobs with an incomplete consultation.
    if (taxTypeJobWithIncompleteConsultation) {
      this.consultationVm.incompleteConsultationCompletionDialogVm.show()
      return
    }

    this._requestClose(this.job)
  }

  @computed
  private get _isCurrentMemberInConversation() {
    const currentMemberPublicId = this.jobWorkspaceMember.user.publicId
    return this.job.conversation?.participants.some(
      (p) => p.member.user.publicId === currentMemberPublicId,
    )
  }

  /**
   * Ensures the terms of use are accepted for the workspace in which the job resides.
   */
  private async _ensureTermsAcceptedForJob() {
    if (this.job.workspace.config.termsOfService.hideOnWortal) {
      return
    }

    const id = this.job.workspace.config.termsOfService.customTermsEnabled
      ? this.job.workspace.id
      : null

    await this._ensureTermsAccepted(id)
  }
}

function countOrNothing(count: number) {
  return count > 0 ? String(count) : undefined
}

const detailsOpenKey = 'Job details rail open'

function readDetailsOpen() {
  return localStorage.getItem(detailsOpenKey) === 'true'
}
